import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card, Input, Button } from "../components";
import { apiPost } from "../lib/api";
import { PUBLIC_CONSENT_VERSION } from "../../../../src/shared/contracts/public-consent.js";

interface RegisterResult {
  token: string;
  refreshToken?: string;
  userId: string;
  email: string;
  role: string;
  pendingApproval?: boolean;
  loginUrl?: string;
}

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledEmail = useMemo(() => searchParams.get("email") ?? "", [searchParams]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [pendingApproval, setPendingApproval] = useState(false);
  const [loginUrl, setLoginUrl] = useState("");

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setPendingApproval(false);
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!acceptedTerms) {
      setError("Please acknowledge the early access terms and privacy notice.");
      return;
    }
    setLoading(true);
    try {
      const result = await apiPost<RegisterResult>("/api/auth/register", {
        email,
        name: name.trim() || email,
        password,
        acceptedTerms: true,
        termsVersion: PUBLIC_CONSENT_VERSION,
      });
      setLoginUrl(result.loginUrl ?? "");
      if (result.pendingApproval) {
        setPendingApproval(true);
        setSuccessMessage("Account created. Waitlist approval is still required before sign-in.");
      } else {
        setSuccessMessage("Account created. You can now sign in with these credentials.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--c-surface-2)",
        padding: "1rem",
      }}
    >
      <div style={{ width: "100%", maxWidth: 460 }}>
        <Card>
          <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "0.75rem",
                fontSize: "0.65rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "4px 8px",
                borderRadius: 999,
                border: "1px solid var(--c-border)",
                color: "var(--c-text-faint)",
                backgroundColor: "var(--c-surface)",
              }}
            >
              Agent Me Access
            </div>
            <h1
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontSize: "2rem",
                fontWeight: 400,
                color: "var(--c-text)",
                marginBottom: "0.3rem",
              }}
            >
              Create Your Account
            </h1>
            <p style={{ color: "var(--c-text-secondary)", fontSize: "0.9rem" }}>
              Use this once after waitlist approval.
            </p>
          </div>

          {!successMessage ? (
            <form onSubmit={handleRegister}>
              <div style={{ marginBottom: "0.75rem" }}>
                <Input
                  label="Name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div style={{ marginBottom: "0.75rem" }}>
                <Input
                  label="Email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div style={{ marginBottom: "0.75rem" }}>
                <Input
                  label="Password"
                  type="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div style={{ marginBottom: "0.75rem" }}>
                <Input
                  label="Confirm password"
                  type="password"
                  placeholder="Repeat password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <label
                style={{
                  display: "flex",
                  gap: "0.6rem",
                  alignItems: "flex-start",
                  marginBottom: "0.75rem",
                  color: "var(--c-text-secondary)",
                  fontSize: "0.82rem",
                  lineHeight: 1.45,
                }}
              >
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  style={{ marginTop: "0.18rem" }}
                  required
                />
                <span>
                  I acknowledge the{" "}
                  <Link to="/privacy" style={{ color: "var(--c-primary)" }}>
                    Privacy Notice
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms" style={{ color: "var(--c-primary)" }}>
                    Terms of Use
                  </Link>
                  , including the early access and platform-managed inference budget terms.
                </span>
              </label>
              {error ? (
                <p role="alert" style={{ color: "var(--c-danger)", marginBottom: "0.75rem" }}>
                  {error}
                </p>
              ) : null}
              <Button
                type="submit"
                variant="accent"
                className="w-full"
                disabled={loading || !acceptedTerms}
              >
                {loading ? "Creating account..." : "Create account"}
              </Button>
            </form>
          ) : (
            <div style={{ textAlign: "center" }}>
              <p style={{ color: "var(--c-success)", marginBottom: "0.75rem" }}>{successMessage}</p>
              {pendingApproval ? null : (
                <Button
                  variant="accent"
                  className="w-full"
                  onClick={() => {
                    window.location.href = loginUrl || "/login";
                  }}
                >
                  Continue to sign in
                </Button>
              )}
              <Button
                variant="ghost"
                className="w-full"
                style={{ marginTop: "0.5rem" }}
                onClick={() => navigate("/login")}
              >
                Open Adam Login
              </Button>
            </div>
          )}

          <p style={{ color: "var(--c-text-faint)", fontSize: "0.8rem", marginTop: "0.9rem" }}>
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
